import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Box,
  CircularProgress,
  Grid,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import { useError } from '../contexts/ErrorContext';
import { getConfig, updateConfig, ConfigResponse, getLanguage } from '../services/api';

const Config: React.FC = () => {
  const navigate = useNavigate();
  const { showError } = useError();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<ConfigResponse | null>(null);
  const [versionInput, setVersionInput] = useState<number>(0);
  const [messagesVersionInput, setMessagesVersionInput] = useState<number>(0);
  const [teamNamesVersionInput, setTeamNamesVersionInput] = useState<number>(0);
  const [selectedLanguage] = useState<string>(getLanguage());

  const parseNumericField = (value: unknown): number => {
    return typeof value === 'number' ? value : parseInt(String(value)) || 0;
  };

  const applyConfigData = (configData: ConfigResponse) => {
    setConfig(configData);
    setVersionInput(parseNumericField(configData.version));
    setMessagesVersionInput(parseNumericField(configData.messagesVersion));
    setTeamNamesVersionInput(parseNumericField(configData.teamNamesVersion));
  };

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await getConfig();
      console.log('Config API Response:', response);
      console.log('Config API Response Data:', response?.data);
      console.log('Config API Response Data Type:', typeof response?.data);
      
      // Handle different response structures
      let configData: ConfigResponse | null = null;
      
      if (response && response.data) {
        const data = response.data as any; // Use any to handle different response structures
        
        // Check if data is directly the config object
        if (data.version !== undefined) {
          configData = data as ConfigResponse;
        } 
        // Check if data is nested in another property (for some API responses)
        else if (data.data && typeof data.data === 'object' && data.data.version !== undefined) {
          configData = data.data as ConfigResponse;
        }
        // Check if response.data itself is the config
        else if (typeof data === 'object' && 'version' in data) {
          configData = data as ConfigResponse;
        }
      }
      
      if (configData) {
        console.log('Setting config data:', configData);
        applyConfigData(configData);
      } else {
        console.warn('No valid config data found in response');
        setConfig(null);
        setVersionInput(0);
        setMessagesVersionInput(0);
        setTeamNamesVersionInput(0);
      }
    } catch (err) {
      console.error('Error fetching config:', err);
      showError('Failed to fetch config. Please try again.');
      setConfig(null);
      setVersionInput(0);
      setMessagesVersionInput(0);
      setTeamNamesVersionInput(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('Config component mounted');
    fetchConfig();
  }, [selectedLanguage]);

  // Sync inputs with config when config changes
  useEffect(() => {
    if (config) {
      const versionValue = parseNumericField(config.version);
      const messagesVersionValue = parseNumericField(config.messagesVersion);
      const teamNamesVersionValue = parseNumericField(config.teamNamesVersion);

      if (versionInput !== versionValue) {
        setVersionInput(versionValue);
      }
      if (messagesVersionInput !== messagesVersionValue) {
        setMessagesVersionInput(messagesVersionValue);
      }
      if (teamNamesVersionInput !== teamNamesVersionValue) {
        setTeamNamesVersionInput(teamNamesVersionValue);
      }
    }
  }, [config]);

  const handleUpdateConfig = async () => {
    try {
      setSaving(true);
      const response = await updateConfig({
        version: versionInput,
        messagesVersion: messagesVersionInput,
        teamNamesVersion: teamNamesVersionInput,
      });
      console.log('Config Update Response:', response);
      
      // Handle different response structures
      let configData: ConfigResponse | null = null;
      
      if (response && response.data) {
        const data = response.data as any; // Use any to handle different response structures
        
        // Check if data is directly the config object
        if (data.version !== undefined) {
          configData = data as ConfigResponse;
        } 
        // Check if data is nested in another property (for some API responses)
        else if (data.data && typeof data.data === 'object' && data.data.version !== undefined) {
          configData = data.data as ConfigResponse;
        }
        // Check if response.data itself is the config
        else if (typeof data === 'object' && 'version' in data) {
          configData = data as ConfigResponse;
        }
      }
      
      if (configData) {
        applyConfigData(configData);
      } else {
        setConfig(null);
        setVersionInput(0);
        setMessagesVersionInput(0);
        setTeamNamesVersionInput(0);
      }
    } catch (err) {
      console.error('Error updating config:', err);
      showError('Failed to update config. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" component="h1">
            Config Settings
          </Typography>
        </Box>
        
        <Typography variant="h6" gutterBottom>
          Manage application configuration ({selectedLanguage.toUpperCase()})
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {/* Current Version Section */}
            <Grid item xs={12}>
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 3, 
                  backgroundColor: '#f8f9fa',
                  borderLeft: '4px solid #1976d2'
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
                  Current Version Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {config ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', minWidth: 140 }}>
                        Version:
                      </Typography>
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          color: '#1976d2', 
                          fontWeight: 'bold',
                          backgroundColor: '#e3f2fd',
                          px: 2,
                          py: 0.5,
                          borderRadius: 1
                        }}
                      >
                        {config.version}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', minWidth: 140 }}>
                        Messages Version:
                      </Typography>
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          color: '#1976d2', 
                          fontWeight: 'bold',
                          backgroundColor: '#e3f2fd',
                          px: 2,
                          py: 0.5,
                          borderRadius: 1
                        }}
                      >
                        {config.messagesVersion}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', minWidth: 140 }}>
                        Team Names Version:
                      </Typography>
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          color: '#1976d2', 
                          fontWeight: 'bold',
                          backgroundColor: '#e3f2fd',
                          px: 2,
                          py: 0.5,
                          borderRadius: 1
                        }}
                      >
                        {config.teamNamesVersion}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', minWidth: 140 }}>
                        Created At:
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {formatDate(config.createdAt)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', minWidth: 140 }}>
                        Updated At:
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {formatDate(config.updatedAt)}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography color="text.secondary">
                    No configuration data available
                  </Typography>
                )}
                
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchConfig}
                  disabled={loading}
                  sx={{ mt: 2 }}
                >
                  Refresh
                </Button>
              </Paper>
            </Grid>

            {/* Update Version Section */}
            <Grid item xs={12}>
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 3, 
                  backgroundColor: '#fff8e1',
                  borderLeft: '4px solid #ff9800'
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ color: '#f57c00' }}>
                  Update Configuration
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    label="New Version"
                    type="number"
                    value={versionInput !== undefined && versionInput !== null ? versionInput : (config?.version ?? '')}
                    onChange={(e) => {
                      const val = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                      setVersionInput(val);
                    }}
                    inputProps={{ min: 0 }}
                    sx={{ 
                      minWidth: 150,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white'
                      }
                    }}
                  />
                  <TextField
                    label="New Messages Version"
                    type="number"
                    value={messagesVersionInput !== undefined && messagesVersionInput !== null ? messagesVersionInput : (config?.messagesVersion ?? '')}
                    onChange={(e) => {
                      const val = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                      setMessagesVersionInput(val);
                    }}
                    inputProps={{ min: 0 }}
                    sx={{ 
                      minWidth: 200,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white'
                      }
                    }}
                  />
                  <TextField
                    label="New Team Names Version"
                    type="number"
                    value={teamNamesVersionInput !== undefined && teamNamesVersionInput !== null ? teamNamesVersionInput : (config?.teamNamesVersion ?? '')}
                    onChange={(e) => {
                      const val = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                      setTeamNamesVersionInput(val);
                    }}
                    inputProps={{ min: 0 }}
                    sx={{ 
                      minWidth: 200,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white'
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    onClick={handleUpdateConfig}
                    disabled={saving}
                    sx={{ 
                      height: 56,
                      px: 3
                    }}
                  >
                    {saving ? 'Saving...' : 'Save Configuration'}
                  </Button>
                </Box>
                
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  Note: Updating the version will trigger a configuration refresh for all clients. Updating messages version will trigger a messages refresh for all clients. Updating team names version will trigger a team names refresh for all clients.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Paper>
    </Container>
  );
};

export default Config;

